import { ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtDateTime } from '../../shared/format'
import type { Role } from '../types'
import type { useAccessLookups } from '../useLookups'
import { AvatarStack, ScopeBadge, StatusBadge } from './AccessBits'
import { RoleRowActions } from './RoleRowActions'
import type { RoleCommands } from './useRoleCommands'

type Props = {
  roles: Role[]
  loading: boolean
  selectedId: string | null
  canWrite: boolean
  lookups: ReturnType<typeof useAccessLookups>
  commands: RoleCommands
  onSelect: (role: Role) => void
  onEdit: (role: Role) => void
  onAssign: (role: Role) => void
}

function RoleIcon({ selected }: { selected: boolean }) {
  return (
    <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue', selected && 'bg-brand-orange/12 text-brand-orange')}>
      <ShieldCheck className="size-4" />
    </span>
  )
}

const selectedRow = 'bg-brand-blue/[0.06] hover:bg-brand-blue/[0.08] data-[state=selected]:bg-brand-blue/[0.06]'

export function RolesTable({ roles, loading, selectedId, canWrite, lookups, commands, onSelect, onEdit, onAssign }: Props) {
  const { t } = useTranslation()
  const actions = (role: Role) => <RoleRowActions role={role} canWrite={canWrite} commands={commands} onEdit={onEdit} onAssign={onAssign} />

  if (loading) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
    )
  }

  return (
    <>
      <ul className="divide-y md:hidden">
        {roles.map((role) => {
          const selected = role.id === selectedId
          return (
            <li key={role.id} onClick={() => onSelect(role)} className={cn('cursor-pointer space-y-3 p-3 transition-colors hover:bg-muted/50', selected && selectedRow)}>
              <div className="flex items-start gap-3">
                <RoleIcon selected={selected} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-medium">{role.name}</span>
                    {role.isSystem ? <Badge tone="neutral">{t('access.roles.systemBadge')}</Badge> : null}
                  </div>
                  <p className="line-clamp-2 text-xs text-muted-foreground">{role.description}</p>
                </div>
                {actions(role)}
              </div>
              <div className="flex flex-wrap items-center gap-2 pl-12">
                <StatusBadge status={role.status} />
                <ScopeBadge scope={role.scope} />
                <span className="text-xs text-muted-foreground">{t('access.roles.columns.userCount')}: {role.userCount}</span>
                <span className="text-xs text-muted-foreground">{t('access.roles.permissionCount', { count: role.permissions.length })}</span>
                <div className="ml-auto">
                  <AvatarStack names={lookups.userNames(role)} />
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <div className="hidden md:block">
        <Table>
          <THead>
            <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
              <TH>{t('access.roles.columns.name')}</TH>
              <TH className="hidden 2xl:table-cell">{t('access.roles.columns.description')}</TH>
              <TH className="text-right">{t('access.roles.columns.userCount')}</TH>
              <TH>{t('access.roles.columns.users')}</TH>
              <TH className="hidden lg:table-cell">{t('access.roles.columns.permissions')}</TH>
              <TH>{t('access.roles.columns.scope')}</TH>
              <TH className="hidden xl:table-cell">{t('access.roles.columns.updated')}</TH>
              <TH>{t('access.roles.columns.status')}</TH>
              <TH className="text-right">{t('access.roles.columns.actions')}</TH>
            </TR>
          </THead>
          <TBody>
            {roles.map((role) => {
              const selected = role.id === selectedId
              return (
                <TR key={role.id} data-state={selected ? 'selected' : undefined} onClick={() => onSelect(role)} className={cn('cursor-pointer', selected && selectedRow)}>
                  <TD>
                    <div className="flex items-center gap-3">
                      <RoleIcon selected={selected} />
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="whitespace-nowrap font-medium">{role.name}</span>
                        {role.isSystem ? <Badge tone="neutral">{t('access.roles.systemBadge')}</Badge> : null}
                      </div>
                    </div>
                  </TD>
                  <TD className="hidden max-w-xs truncate text-muted-foreground 2xl:table-cell">{role.description}</TD>
                  <TD className="text-right tabular-nums">{role.userCount}</TD>
                  <TD>
                    <AvatarStack names={lookups.userNames(role)} />
                  </TD>
                  <TD className="hidden tabular-nums lg:table-cell">{role.permissions.length}</TD>
                  <TD>
                    <ScopeBadge scope={role.scope} />
                  </TD>
                  <TD className="hidden xl:table-cell">
                    <div className="whitespace-nowrap text-sm tabular-nums">{fmtDateTime(role.updatedAt)}</div>
                    <div className="text-xs text-muted-foreground">{role.updatedBy?.fullName || role.updatedBy?.email || '—'}</div>
                  </TD>
                  <TD>
                    <StatusBadge status={role.status} />
                  </TD>
                  <TD className="text-right">{actions(role)}</TD>
                </TR>
              )
            })}
          </TBody>
        </Table>
      </div>
    </>
  )
}
